// BL598 Phase 1A + BL600/BL608 (Option A) — `gridctl pickup <entity-name-or-id> [target-dir]`.
//
// `pickup` is the canonical takeover verb in the 3-verb collaboration model. It
// does two things, in order:
//
//   1. Records the grant/claim server-side by firing
//      `POST /api/v2/entities/:id/pickup`. The server AUTO-DETECTS the target
//      (BL600 + BL608): a regular grid entity records a COLLABORATOR grant (so a
//      later `plug` of that grid's entity is authorized); an anon-minted
//      Guest-Grid drop transfers OWNERSHIP to the caller's grid. The CLI does
//      NOT branch on target type — the server picks the path.
//   2. Lays down the working tree locally (download source + write
//      `cloudgrid.yaml` + `.cloudgrid/link.json`), exactly as before, so the
//      next `gridctl plug` deploys against the right entity.
//
// Before Option A, step 1 was skipped — pickup was a local clone only and never
// told the server, so a later `plug` of another grid's entity failed with
// FORBIDDEN_ROLE (the collaborator grant was never recorded). Firing `/pickup`
// first closes that gap.
//
// `--no-bind` skips step 2 (records the grant but lays down nothing);
// `--claim-token` passes the claim link for an anon Guest-Grid drop picked up
// from a transport with no anon-session cookie.

import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { notSignedInMessage } from '../active-org.js';
import { CloudGridError } from '../utils.js';
import { runClone } from './clone.js';

/**
 * Response of `POST /api/v2/entities/:id/pickup`. The server returns one of two
 * shapes (collaborator grant vs. ownership transfer) keyed by `role_granted`;
 * the fields below are the union the CLI reads. Unknown extra fields are
 * forward-compatible additions.
 */
interface PickupResponse {
  entity_id: string;
  name: string;
  kind?: string;
  role_granted: 'collaborator' | 'owner';
  grid_slug?: string;
  /** Ownership-transfer path only — the entity's new public home. */
  url?: string;
}

export interface RunPickupOpts {
  client: IV2Client;
  /** Entity slug / id / `grid/slug` to pick up. */
  target: string;
  /** Optional directory to lay the working tree into. Defaults to the name. */
  targetDir?: string;
  cwd?: string;
  /** `--org <slug>` — active-grid override for a bare-slug target. */
  org?: string;
  /** Lay the working tree into a non-empty directory. */
  force?: boolean;
  /** ADR 036 V3 — pick up an older version's source + yaml instead of HEAD. */
  version?: string;
  /** Record the grant only — skip the local working-tree download. */
  noBind?: boolean;
  /** Claim link for an anon Guest-Grid drop (cookie-less transports). */
  claimToken?: string;
}

export async function runPickup(opts: RunPickupOpts): Promise<void> {
  const target = (opts.target ?? '').trim();
  if (target.length === 0) {
    throw new CloudGridError('Provide an entity name or id: gridctl pickup <name>');
  }

  // Scope the client to `--org` when given, so a bare-slug target resolves in
  // the intended grid (the pickup route reads the active-grid header for a bare
  // slug; a UUID / `grid/slug` resolves globally regardless).
  const client = opts.org !== undefined ? opts.client.withOrgSlug(opts.org) : opts.client;

  // ── Step 1: record the grant/claim server-side ──────────────────────────
  const body: { claim_token?: string } = {};
  if (opts.claimToken) body.claim_token = opts.claimToken;

  let picked: PickupResponse;
  try {
    picked = await client.post<PickupResponse>(
      `/api/v2/entities/${encodeURIComponent(target)}/pickup`,
      body,
    );
  } catch (err) {
    if (err instanceof V2ApiError) throw mapPickupError(err, target);
    throw err;
  }

  if (picked.role_granted === 'owner') {
    // Ownership transfer (anon Guest-Grid drop claimed into the caller's grid).
    const where = picked.grid_slug ? ` into ${picked.grid_slug}` : '';
    process.stdout.write(`Claimed ${picked.name}${where} — it's yours now.\n`);
    if (picked.url) process.stdout.write(`  Public URL: ${picked.url}\n`);
  } else {
    // Collaborator grant on an existing grid entity.
    const where = picked.grid_slug ? ` in ${picked.grid_slug}` : '';
    process.stdout.write(`Picked up ${picked.name}${where}. Your next plug updates it.\n`);
  }

  // ── Step 2: lay down the working tree locally ───────────────────────────
  // Address the clone by the server-confirmed entity_id so the local fetch
  // targets the exact entity the grant/claim was recorded against — no second
  // resolution, no ambiguity if the target was a bare slug.
  await runClone({
    client,
    target: picked.entity_id,
    targetDir: opts.targetDir,
    cwd: opts.cwd,
    org: opts.org,
    force: opts.force,
    version: opts.version,
    noBind: opts.noBind,
  });
}

/** Map pickup-endpoint errors to plain, actionable messages (§23 voice). */
function mapPickupError(err: V2ApiError, target: string): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID' ||
    err.code === 'SIGN_UP_REQUIRED'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'NOT_A_GRID_MEMBER') {
    // The server's message already names the grid to ask into; surface it.
    return new CloudGridError(
      err.message || `You're not a member of the grid that owns '${target}'. Ask its owner to invite you first.`,
    );
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      err.message || `Picking up '${target}' needs developer or admin role in its grid.`,
    );
  }
  if (err.code === 'CLAIM_TOKEN_REQUIRED') {
    return new CloudGridError(
      err.message ||
        `'${target}' is an anonymous drop — pass its claim link with --claim-token, or open it from the browser that made the drop.`,
    );
  }
  if (err.code === 'INVALID_CLAIM_TOKEN') {
    return new CloudGridError(err.message || 'That claim link is invalid or has expired.');
  }
  if (err.code === 'ALREADY_CLAIMED') {
    return new CloudGridError(
      `'${target}' was just claimed by someone else. Nothing to pick up.`,
    );
  }
  if (err.code === 'NOT_A_RUNTIME') {
    return new CloudGridError(
      `'${target}' is an Inspiration, not an app or agent. Pick up a runtime, or fork the inspiration instead.`,
    );
  }
  if (err.status === 404 || err.code.endsWith('_NOT_FOUND') || err.code === 'NOT_FOUND') {
    return new CloudGridError(`No entity matched '${target}'. Check the id or grid/slug.`);
  }
  if (err.code === 'RATE_LIMITED') {
    return new CloudGridError('Too many pickups today. Try again later.');
  }
  if (err.code === 'BAD_REQUEST') {
    return new CloudGridError(
      err.message ||
        `Couldn't resolve '${target}'. Pass a full id or grid/slug, or set an active grid with --org.`,
    );
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run cloudgrid feedback if it persists.`,
  );
}

export const pickupCommand: CommanderCommand = new CommanderCommand('pickup')
  .description(
    'Adopt an entity: record the grant/claim server-side, then download its source + cloudgrid.yaml and link the folder to it.',
  )
  .argument('<name>', 'Entity slug, id, or grid/slug to pick up')
  .argument('[target-dir]', 'Directory to pick up into (defaults to the entity name)')
  .option('--org <slug>', 'Grid to resolve the entity in (defaults to the active grid)')
  .option('--force', 'Pick up into a non-empty directory')
  .option('--version <id>', "Pick up an older version's source + cloudgrid.yaml instead of HEAD")
  .option('--claim-token <token>', 'Claim link for an anonymous drop (when picking up a Guest-Grid drop)')
  .option('--no-bind', 'Record the grant only — skip the source download, cloudgrid.yaml, and the link')
  .action(
    async (
      name: string,
      targetDir: string | undefined,
      opts: { org?: string; force?: boolean; bind?: boolean; version?: string; claimToken?: string },
    ) => {
      try {
        const client = await getV2Client(opts.org);
        await runPickup({
          client,
          target: name,
          targetDir,
          org: opts.org,
          force: opts.force,
          version: opts.version,
          claimToken: opts.claimToken,
          // Commander maps `--no-bind` to `opts.bind === false`.
          noBind: opts.bind === false,
        });
      } catch (err) {
        await handleV2Error(err);
      }
    },
  );
